import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import API_ENDPOINTS from '../config/api';

// Kind -> single-letter badge + color token, used for the small chips that
// stand in for the old canvas nodes.
const KIND_META = {
  flow: { letter: 'F', token: '--jedi-blue' },
  object: { letter: 'O', token: '--yoda-green' },
  apex: { letter: 'A', token: '--droid-gold' },
  workflow: { letter: 'W', token: '--gray-500' },
};

const PREFIX_TO_KIND = {
  flow: 'flow',
  obj: 'object',
  apex: 'apex',
  wf: 'workflow',
};

const stripPrefix = (id) => {
  if (typeof id !== 'string') return id;
  const idx = id.indexOf(':');
  return idx === -1 ? id : id.slice(idx + 1);
};

const kindOf = (id) => {
  if (typeof id !== 'string') return 'workflow';
  const prefix = id.split(':')[0];
  return PREFIX_TO_KIND[prefix] || 'workflow';
};

// Same edge -> human detail text the old canvas edge labels used, minus the
// canvas.
const edgeDetailText = (edge) => {
  const detail = edge && edge.detail;
  if (edge.type === 'writes') {
    return (detail && Array.isArray(detail.operations) && detail.operations.join(', ')) || '';
  }
  if (edge.type === 'triggers') {
    if (detail && Array.isArray(detail.events) && detail.events.length > 0) {
      return detail.events.join(', ');
    }
    return (detail && detail.trigger_type) || '';
  }
  if (edge.type === 'calls') {
    if (detail && detail.inferred) return 'inferred';
    return (detail && detail.connection_type) || '';
  }
  return '';
};

const risksByObject = (risks) => {
  const map = new Map();
  (risks || []).forEach((risk) => {
    const key = risk.object || risk.flow || 'unknown';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(risk);
  });
  return map;
};

// A small type-colored badge + mono name. Clickable when onPivot is given -
// this is the only interactive primitive left after removing the canvas.
const Chip = ({ id, kindHint, onPivot }) => {
  const kind = kindHint || kindOf(id);
  const meta = KIND_META[kind] || KIND_META.workflow;
  const content = (
    <>
      <span className="ge-chip-badge" style={{ color: `var(${meta.token})`, borderColor: `var(${meta.token})` }}>
        {meta.letter}
      </span>
      <span className="ge-chip-name">{stripPrefix(id)}</span>
    </>
  );
  if (!onPivot) {
    return <span className="ge-chip">{content}</span>;
  }
  return (
    <button type="button" className="ge-chip ge-chip-clickable" onClick={() => onPivot(id)}>
      {content}
    </button>
  );
};

const LoopChainRow = ({ cycle, onPivot }) => {
  const path = cycle.path || [];
  return (
    <div className="ge-loop-row" data-testid="loop-chain-row">
      <div className="ge-loop-chain">
        {path.map((id, i) => (
          <React.Fragment key={`${id}-${i}`}>
            {i > 0 && <span className="ge-chain-arrow">&rarr;</span>}
            <Chip id={id} onPivot={onPivot} />
          </React.Fragment>
        ))}
        <span className="ge-chain-arrow ge-chain-close">&#8635;</span>
      </div>
      {cycle.selfLoop && <span className="ge-tag-selfloop">self-loop</span>}
    </div>
  );
};

const RiskRow = ({ risk, onPivot }) => {
  const hasPath = Array.isArray(risk.path) && risk.path.length > 0;
  const viaKindHint = risk.viaKind === 'apex' || risk.viaKind === 'workflow' ? risk.viaKind : undefined;
  return (
    <div className="ge-risk-row">
      <div className="ge-risk-cell ge-risk-via">
        {hasPath ? (
          <span className="ge-loop-chain ge-loop-chain-inline">
            {risk.path.map((id, i) => (
              <React.Fragment key={`${id}-${i}`}>
                {i > 0 && <span className="ge-chain-arrow">&rarr;</span>}
                <Chip id={id} onPivot={onPivot} />
              </React.Fragment>
            ))}
          </span>
        ) : risk.via ? (
          <Chip id={risk.via} kindHint={viaKindHint} onPivot={onPivot} />
        ) : (
          <span className="ge-chip">
            <span className="ge-chip-name">inferred-chain</span>
          </span>
        )}
      </div>
      <div className="ge-risk-cell ge-risk-automation">{risk.flow ? stripPrefix(risk.flow) : '—'}</div>
      <div className="ge-risk-cell ge-risk-description">{risk.description}</div>
    </div>
  );
};

const GraphExplorer = ({ getAuthHeaders, initialNode }) => {
  const [allNodes, setAllNodes] = useState([]);
  const [loadedNodes, setLoadedNodes] = useState(false);
  const [loops, setLoops] = useState({ confirmed: [], risks: [], clusters: [], truncated: false });
  const [loadedLoops, setLoadedLoops] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [depth, setDepth] = useState(1);
  const [centerId, setCenterId] = useState(null);
  const [neighborhoodNodes, setNeighborhoodNodes] = useState([]);
  const [neighborhoodEdges, setNeighborhoodEdges] = useState([]);
  const [neighborhoodTruncated, setNeighborhoodTruncated] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState([]);

  const connectionsRef = useRef(null);

  const fetchNodes = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_GRAPH_NODES, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to load graph nodes (${response.status})`);
      }
      const data = await response.json();
      const nodes = Array.isArray(data) ? data : data.nodes || [];
      setAllNodes(nodes);
    } catch (err) {
      setError(err.message || 'Failed to load graph nodes');
    } finally {
      setLoadedNodes(true);
    }
  }, [getAuthHeaders]);

  const fetchLoops = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_GRAPH_LOOPS, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to load loops (${response.status})`);
      }
      const data = await response.json();
      setLoops({
        confirmed: data.confirmed || [],
        risks: data.risks || [],
        // Older payloads (pre-clusters) omit this key entirely; guard so the
        // page falls back to the flat, non-cluster rendering instead of
        // crashing.
        clusters: Array.isArray(data.clusters) ? data.clusters : [],
        truncated: !!data.truncated,
      });
    } catch (err) {
      setError(err.message || 'Failed to load loops');
    } finally {
      setLoadedLoops(true);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchNodes();
    fetchLoops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToConnections = useCallback(() => {
    const el = connectionsRef.current;
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const loadNeighborhood = useCallback(
    async (nodeId, atDepth) => {
      setError(null);
      try {
        const response = await fetch(
          API_ENDPOINTS.DOCUMENTATION_GRAPH_NEIGHBORHOOD(nodeId, atDepth != null ? atDepth : depth),
          { headers: getAuthHeaders() }
        );
        if (!response.ok) {
          throw new Error(`Failed to load neighborhood (${response.status})`);
        }
        const data = await response.json();
        setCenterId(nodeId);
        setNeighborhoodNodes(data.nodes || []);
        setNeighborhoodEdges(data.edges || []);
        setNeighborhoodTruncated(!!data.truncated);
        setBreadcrumb((prev) => [nodeId, ...prev.filter((id) => id !== nodeId)].slice(0, 5));
      } catch (err) {
        setError(err.message || 'Failed to load neighborhood');
      }
    },
    [depth, getAuthHeaders]
  );

  const handlePivot = useCallback(
    (nodeId) => {
      loadNeighborhood(nodeId);
      scrollToConnections();
    },
    [loadNeighborhood, scrollToConnections]
  );

  // When the caller hands us a starting node (e.g. jumping in from the Flows
  // tab), load its neighborhood on mount and whenever it changes.
  useEffect(() => {
    if (initialNode) {
      loadNeighborhood(initialNode);
      scrollToConnections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialNode]);

  const handleDepthChange = useCallback(
    (nextDepth) => {
      setDepth(nextDepth);
      if (centerId) {
        loadNeighborhood(centerId, nextDepth);
      }
    },
    [centerId, loadNeighborhood]
  );

  const filteredResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return allNodes
      .filter((n) => n.id.toLowerCase().includes(q) || (n.label || '').toLowerCase().includes(q))
      .slice(0, 25);
  }, [search, allNodes]);

  const nodeById = useMemo(() => {
    const map = new Map();
    allNodes.forEach((n) => map.set(n.id, n));
    neighborhoodNodes.forEach((n) => map.set(n.id, n));
    return map;
  }, [allNodes, neighborhoodNodes]);

  const connectionSections = useMemo(() => {
    if (!centerId) return [];
    const groups = {
      triggeredBy: [],
      triggers: [],
      writesTo: [],
      writtenBy: [],
      calls: [],
      calledBy: [],
    };
    neighborhoodEdges.forEach((edge) => {
      if (edge.type === 'triggers') {
        if (edge.to === centerId) groups.triggeredBy.push(edge);
        if (edge.from === centerId) groups.triggers.push(edge);
      } else if (edge.type === 'writes') {
        if (edge.from === centerId) groups.writesTo.push(edge);
        if (edge.to === centerId) groups.writtenBy.push(edge);
      } else if (edge.type === 'calls') {
        if (edge.from === centerId) groups.calls.push(edge);
        if (edge.to === centerId) groups.calledBy.push(edge);
      }
    });
    return [
      { key: 'triggeredBy', label: 'Triggered by', edges: groups.triggeredBy, otherKey: 'from' },
      { key: 'triggers', label: 'Triggers', edges: groups.triggers, otherKey: 'to' },
      { key: 'writesTo', label: 'Writes to', edges: groups.writesTo, otherKey: 'to' },
      { key: 'writtenBy', label: 'Written by', edges: groups.writtenBy, otherKey: 'from' },
      { key: 'calls', label: 'Calls', edges: groups.calls, otherKey: 'to' },
      { key: 'calledBy', label: 'Called by', edges: groups.calledBy, otherKey: 'from' },
    ].filter((section) => section.edges.length > 0);
  }, [centerId, neighborhoodEdges]);

  const hasClusters = loops.clusters.length > 0;
  const hasNoScans =
    loadedNodes && loadedLoops && allNodes.length === 0 && loops.confirmed.length === 0 && loops.risks.length === 0;
  const groupedRisks = useMemo(() => [...risksByObject(loops.risks).entries()], [loops.risks]);

  const centerNode = centerId ? nodeById.get(centerId) : null;

  return (
    <div className="ge-page">
      <div className="ge-summary-bar glass-card">
        <div className="ge-summary-counts">
          {loops.confirmed.length} confirmed loop{loops.confirmed.length === 1 ? '' : 's'}
          {' · '}
          {loops.clusters.length} cluster{loops.clusters.length === 1 ? '' : 's'}
          {' · '}
          {loops.risks.length} possible risk{loops.risks.length === 1 ? '' : 's'}
        </div>
        {loops.truncated && (
          <div className="ge-truncation-notice">Loop search hit its cap &mdash; counts are a floor.</div>
        )}
        <p className="ge-runtime-guard">
          Confirmed = every hop is a scanned, matching edge. Salesforce recursion guards may
          still bound this at runtime &mdash; verify before assuming infinite recursion.
        </p>
      </div>

      {error && <div className="ge-error-banner">{error}</div>}

      {hasNoScans && (
        <div className="ge-empty-prompt">No scans yet. Run an object, flow, and automation scan to build the graph.</div>
      )}

      <section className="ge-section">
        <h4 className="ge-section-title">Loop clusters</h4>
        {hasClusters ? (
          <div className="ge-cluster-list">
            {loops.clusters.map((cluster, clusterIndex) => {
              const clusterCycles = loops.confirmed.filter((c) => c.clusterId === cluster.id);
              return (
                <details key={cluster.id} className="ge-cluster-block" open={clusterIndex === 0}>
                  <summary className="ge-cluster-summary">
                    <span className="ge-cluster-label">{cluster.label || cluster.id}</span>
                    <span className="ge-cluster-meta">
                      {(cluster.flows || []).length} flows &middot; {(cluster.objects || []).length} objects &middot;{' '}
                      {cluster.cycleCount || 0} loops &middot; {cluster.selfLoopCount || 0} self-loops
                    </span>
                  </summary>
                  <div className="ge-loop-list">
                    {clusterCycles.map((cycle, i) => (
                      <LoopChainRow key={`cluster-cycle-${cluster.id}-${i}`} cycle={cycle} onPivot={handlePivot} />
                    ))}
                  </div>
                </details>
              );
            })}
          </div>
        ) : (
          <details className="ge-cluster-block" open>
            <summary className="ge-cluster-summary">
              <span className="ge-cluster-label">Loops ({loops.confirmed.length})</span>
            </summary>
            <div className="ge-loop-list">
              {loops.confirmed.map((cycle, i) => (
                <LoopChainRow key={`confirmed-${i}`} cycle={cycle} onPivot={handlePivot} />
              ))}
            </div>
          </details>
        )}
      </section>

      <section className="ge-section">
        <details className="ge-risks-block">
          <summary className="ge-section-title ge-risks-summary">Possible risks ({loops.risks.length})</summary>
          {groupedRisks.map(([objectId, risks]) => (
            <div key={objectId} className="ge-risk-group">
              <div className="ge-risk-group-header">
                {stripPrefix(objectId)} ({risks.length})
              </div>
              <div className="ge-risk-table">
                {risks.map((risk, i) => (
                  <RiskRow key={`risk-${objectId}-${i}`} risk={risk} onPivot={handlePivot} />
                ))}
              </div>
            </div>
          ))}
        </details>
      </section>

      <section className="ge-section ge-connections" ref={connectionsRef}>
        <h4 className="ge-section-title">Explore connections</h4>

        <div className="ge-conn-search">
          <input
            type="text"
            placeholder="Search objects, flows, triggers&hellip;"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ge-search-input"
          />
          <div className="ge-depth-toggle">
            <span>Depth</span>
            <button
              type="button"
              className={`ge-depth-btn ${depth === 1 ? 'active' : ''}`}
              onClick={() => handleDepthChange(1)}
            >
              1
            </button>
            <button
              type="button"
              className={`ge-depth-btn ${depth === 2 ? 'active' : ''}`}
              onClick={() => handleDepthChange(2)}
            >
              2
            </button>
          </div>
        </div>

        {search.trim() && (
          <div className="ge-search-results">
            {filteredResults.length === 0 && <div className="ge-search-empty">No matches.</div>}
            {filteredResults.map((n) => (
              <Chip key={n.id} id={n.id} kindHint={n.kind} onPivot={handlePivot} />
            ))}
          </div>
        )}

        {breadcrumb.length > 0 && (
          <div className="ge-breadcrumb">
            {breadcrumb.map((id, i) => (
              <React.Fragment key={id}>
                {i > 0 && <span className="ge-breadcrumb-sep">&rsaquo;</span>}
                <button type="button" className="ge-breadcrumb-item" onClick={() => loadNeighborhood(id)}>
                  {stripPrefix(id)}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {!centerId && (
          <p className="ge-conn-placeholder">Search for a node or click a chip above to explore its connections.</p>
        )}

        {centerId && (
          <div className="ge-entity">
            <div className="ge-entity-header">
              <Chip id={centerId} kindHint={centerNode && centerNode.kind} onPivot={handlePivot} />
              <span className="ge-entity-label">{(centerNode && centerNode.label) || stripPrefix(centerId)}</span>
              <span className="ge-entity-kind">{(centerNode && centerNode.kind) || kindOf(centerId)}</span>
            </div>

            {neighborhoodTruncated && (
              <div className="ge-truncation-notice">Neighborhood truncated &mdash; showing partial results.</div>
            )}

            {connectionSections.length === 0 && (
              <p className="ge-conn-placeholder">No connections found at this depth.</p>
            )}

            {connectionSections.map((section) => (
              <div key={section.key} className="ge-conn-section">
                <div className="ge-conn-section-label">{section.label}</div>
                <div className="ge-conn-rows">
                  {section.edges.map((edge, i) => (
                    <div key={`${section.key}-${i}`} className="ge-conn-row">
                      <Chip id={edge[section.otherKey]} onPivot={handlePivot} />
                      <span className="ge-conn-detail">{edgeDetailText(edge)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .ge-page {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1rem 0 3rem;
  }

  .ge-summary-bar {
    padding: 1rem 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .ge-summary-counts {
    font-family: var(--font-mono);
    font-size: 0.95rem;
    color: var(--star-white);
  }

  .ge-truncation-notice {
    font-size: 0.75rem;
    color: var(--droid-gold);
  }

  .ge-runtime-guard {
    margin: 0;
    font-size: 0.78rem;
    color: var(--gray-500);
    line-height: 1.4;
  }

  .ge-error-banner {
    color: var(--sith-red);
    font-size: 0.85rem;
    background: rgba(0,0,0,0.2);
    border: 1px solid var(--sith-red);
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
  }

  .ge-empty-prompt {
    color: var(--gray-500);
    font-size: 0.85rem;
  }

  .ge-section {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .ge-section-title {
    margin: 0;
    color: var(--gray-300);
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    cursor: default;
  }

  .ge-risks-summary {
    cursor: pointer;
  }

  .ge-cluster-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .ge-cluster-block {
    border: 1px solid var(--gray-800);
    border-radius: 6px;
    padding: 0 0.85rem;
  }

  .ge-cluster-summary {
    padding: 0.65rem 0;
    cursor: pointer;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.6rem;
    list-style: none;
  }

  .ge-cluster-summary::-webkit-details-marker {
    display: none;
  }

  .ge-cluster-label {
    font-weight: 600;
    color: var(--star-white);
    font-size: 0.9rem;
  }

  .ge-cluster-meta {
    font-size: 0.75rem;
    color: var(--gray-500);
    font-family: var(--font-mono);
  }

  .ge-loop-list {
    display: flex;
    flex-direction: column;
    padding-bottom: 0.5rem;
  }

  .ge-loop-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.5rem 0;
    border-top: 1px solid var(--gray-800);
  }

  .ge-loop-chain {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.3rem;
  }

  .ge-loop-chain-inline {
    display: inline-flex;
  }

  .ge-chain-arrow {
    color: var(--gray-500);
    font-size: 0.8rem;
  }

  .ge-chain-close {
    color: var(--gray-400);
  }

  .ge-tag-selfloop {
    font-size: 0.68rem;
    color: var(--gray-500);
    border: 1px solid var(--gray-700);
    border-radius: 999px;
    padding: 0.05rem 0.5rem;
  }

  .ge-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    background: transparent;
    border: none;
    padding: 0;
    font: inherit;
    color: var(--star-white);
  }

  .ge-chip-clickable {
    cursor: pointer;
  }

  .ge-chip-clickable:hover .ge-chip-name {
    color: var(--force-cyan);
  }

  .ge-chip-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.1rem;
    height: 1.1rem;
    border: 1px solid;
    border-radius: 3px;
    font-size: 0.62rem;
    font-weight: 700;
    line-height: 1;
  }

  .ge-chip-name {
    font-family: var(--font-mono);
    font-size: 0.8rem;
  }

  .ge-risks-block {
    border: 1px solid var(--gray-800);
    border-radius: 6px;
    padding: 0 0.85rem 0.5rem;
  }

  .ge-risk-group {
    margin-top: 0.6rem;
  }

  .ge-risk-group-header {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--gray-400);
    margin-bottom: 0.3rem;
  }

  .ge-risk-table {
    display: flex;
    flex-direction: column;
  }

  .ge-risk-row {
    display: grid;
    grid-template-columns: minmax(140px, 1fr) minmax(100px, 0.6fr) 2fr;
    gap: 0.75rem;
    align-items: baseline;
    padding: 0.45rem 0;
    border-top: 1px solid var(--gray-800);
    font-size: 0.8rem;
  }

  .ge-risk-automation {
    font-family: var(--font-mono);
    color: var(--gray-300);
  }

  .ge-risk-description {
    color: var(--gray-300);
  }

  .ge-connections {
    border: 1px solid var(--gray-800);
    border-radius: 6px;
    padding: 1rem 1.1rem;
  }

  .ge-conn-search {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .ge-search-input {
    flex: 1;
    min-width: 220px;
    background: rgba(0,0,0,0.2);
    border: 1px solid var(--gray-700);
    border-radius: 6px;
    padding: 0.5rem 0.6rem;
    color: var(--star-white);
    font-size: 0.85rem;
  }

  .ge-depth-toggle {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8rem;
    color: var(--gray-500);
  }

  .ge-depth-btn {
    background: transparent;
    border: 1px solid var(--gray-700);
    color: var(--gray-500);
    border-radius: 4px;
    padding: 0.15rem 0.5rem;
    cursor: pointer;
  }

  .ge-depth-btn.active {
    border-color: var(--force-cyan);
    color: var(--force-cyan);
  }

  .ge-search-results {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    margin-top: 0.6rem;
  }

  .ge-search-empty {
    color: var(--gray-500);
    font-size: 0.8rem;
  }

  .ge-breadcrumb {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.78rem;
    margin-top: 0.75rem;
  }

  .ge-breadcrumb-item {
    background: transparent;
    border: none;
    color: var(--force-cyan);
    cursor: pointer;
    padding: 0;
    font: inherit;
  }

  .ge-breadcrumb-sep {
    color: var(--gray-500);
  }

  .ge-conn-placeholder {
    color: var(--gray-500);
    font-size: 0.82rem;
    margin-top: 0.75rem;
  }

  .ge-entity {
    margin-top: 0.9rem;
  }

  .ge-entity-header {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding-bottom: 0.6rem;
    border-bottom: 1px solid var(--gray-800);
  }

  .ge-entity-label {
    font-weight: 600;
    color: var(--star-white);
    font-size: 0.95rem;
  }

  .ge-entity-kind {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--gray-500);
  }

  .ge-conn-section {
    margin-top: 0.7rem;
  }

  .ge-conn-section-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--gray-500);
    margin-bottom: 0.3rem;
  }

  .ge-conn-rows {
    display: flex;
    flex-direction: column;
  }

  .ge-conn-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.35rem 0;
    border-top: 1px solid var(--gray-800);
    font-size: 0.8rem;
  }

  .ge-conn-detail {
    color: var(--gray-500);
    font-size: 0.75rem;
  }
`;

export default GraphExplorer;
